import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage'; 
import { getCurrentUser } from 'aws-amplify/auth';
import { getCourse, listUserNotes, listLessons } from '../graphql/queries';
import { createUserNote, deleteUserNote } from '../graphql/mutations';
import { View, Heading, Flex, Image, Button, Loader, Text, TextAreaField, Card } from '@aws-amplify/ui-react';
import { FiArrowLeft, FiShield, FiEdit3, FiSave, FiTrash2, FiPlay, FiX, FiDownload } from 'react-icons/fi';
import logo from '../logo.png';

const CoursePlayer = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const client = useMemo(() => generateClient(), []);
    
    const [course, setCourse] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [activeLesson, setActiveLesson] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showNotes, setShowNotes] = useState(false);
    const [currentNote, setCurrentNote] = useState(""); 
    const [notesHistory, setNotesHistory] = useState([]); 
    const [currentUser, setCurrentUser] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const downloadFile = async (key) => {
        if (!key) return;
        try {
            const cleanPath = key.startsWith('public/') ? key : `public/${key}`;
            const link = await getUrl({ path: cleanPath });
            window.open(link.url.toString(), '_blank');
        } catch (err) {
            alert("❌ Error al acceder al archivo.");
        }
    };

    const loadNotes = useCallback(async (lessonId, userId) => {
        if (!lessonId || !userId) return;
        try {
            const notesRes = await client.graphql({
                query: listUserNotes,
                variables: { 
                    filter: { 
                        bookID: { eq: lessonId }, 
                        owner: { eq: userId } 
                    } 
                },
                authMode: 'userPool'
            });
            const items = notesRes.data.listUserNotes.items || [];
            // ORDEN: De la más reciente arriba (b) a la más vieja abajo (a)
            setNotesHistory([...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (err) { console.error("❌ Error notas:", err); }
    }, [client]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        const init = async () => {
            try {
                const user = await getCurrentUser();
                setCurrentUser(user);
                const courseRes = await client.graphql({ query: getCourse, variables: { id: courseId }, authMode: 'userPool' });
                setCourse(courseRes.data.getCourse);
                const lessonsRes = await client.graphql({ query: listLessons, variables: { filter: { courseID: { eq: courseId } } }, authMode: 'userPool' });
                const sorted = [...lessonsRes.data.listLessons.items].sort((a, b) => (a.order || 0) - (b.order || 0));
                setLessons(sorted);
                if (sorted.length > 0) {
                    setActiveLesson(sorted[0]);
                    loadNotes(sorted[0].id, user.username || user.userId);
                }
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        init();
        return () => window.removeEventListener('resize', handleResize);
    }, [courseId, client, loadNotes]);

    useEffect(() => {
        const fetchVideo = async () => {
            if (!activeLesson?.videoUrl) return;
            setVideoUrl(null); 
            if (currentUser) {
                loadNotes(activeLesson.id, currentUser.username || currentUser.userId);
            }
            try {
                const fileName = activeLesson.videoUrl.split('/').pop();
                const dynamicPath = `public/courses/lessons/${courseId}/videos/${fileName}`;
                const signedUrl = await getUrl({ 
                    path: dynamicPath, 
                    options: { expiresIn: 3600 }
                });
                setVideoUrl(signedUrl.url.toString());
            } catch (err) { setVideoUrl(null); }
        };
        fetchVideo();
    }, [activeLesson, courseId, currentUser, loadNotes]);

    const handleSaveNote = async () => {
        if (!currentNote.trim() || !currentUser || !activeLesson) return;
        try {
            await client.graphql({
                query: createUserNote,
                variables: { 
                    input: { 
                        bookID: activeLesson.id, 
                        content: currentNote, 
                        pageNumber: activeLesson?.order || 0,
                        owner: currentUser.username || currentUser.userId
                    } 
                },
                authMode: 'userPool'
            });
            setCurrentNote("");
            loadNotes(activeLesson.id, currentUser.username || currentUser.userId);
        } catch (err) { alert("Error al guardar nota."); }
    };

    const handleDeleteNote = async (id) => {
        try {
            await client.graphql({ query: deleteUserNote, variables: { input: { id } }, authMode: 'userPool' });
            setNotesHistory(prev => prev.filter(n => n.id !== id));
        } catch (err) { console.error(err); }
    };

    if (loading) return <Flex justifyContent="center" padding="100px"><Loader size="large" /><Text>Cargando...</Text></Flex>;

    return (
        <View height="100vh" width="100vw" display="flex" direction="column" style={{ backgroundColor: '#fff', overflow: 'hidden' }}>
            
            <Flex padding="10px 20px" backgroundColor="white" alignItems="center" justifyContent="space-between" boxShadow="0 2px 5px rgba(0,0,0,0.1)" style={{ zIndex: 10, minHeight: '60px' }}>
                <Button onClick={() => navigate('/cursos')} variation="link" color="#001f3f">
                    <FiArrowLeft size="20px" />
                </Button>
                <Image src={logo} alt="Logo" height="30px" />
                <Button onClick={() => setShowNotes(!showNotes)} style={{ backgroundColor: '#D4AF37', color: 'white', borderRadius: '50%' }}>
                    <FiEdit3 />
                </Button>
            </Flex>

            <Flex direction={isMobile ? "column" : "row"} flex="1" style={{ overflow: 'hidden', backgroundColor: '#f9f9f9' }}>
                
                <Flex direction="column" flex="3" style={{ overflowY: 'auto' }}>
                    <View width="100%" backgroundColor="#000" style={{ position: 'relative', maxHeight: isMobile ? '35vh' : '75vh' }}>
                        {videoUrl ? (
                            <video 
                                key={videoUrl} 
                                src={videoUrl} 
                                controls 
                                playsInline
                                controlsList="nodownload noplaybackrate"
                                disablePictureInPicture
                                onContextMenu={(e) => e.preventDefault()}
                                style={{ width: '100%', maxHeight: '100%', display: 'block' }} 
                            />
                        ) : (
                            <Flex direction="column" alignItems="center" justifyContent="center" height="300px">
                                <Loader size="large" />
                                <Text color="white" fontSize="12px">Cargando...</Text>
                            </Flex>
                        )}
                    </View>

                    <View padding="20px">
                        <Heading level={4} color="#001f3f">{course?.title}</Heading>
                        <Flex alignItems="center" gap="15px" marginTop="10px" wrap="wrap" justifyContent="space-between">
                            <View>
                                <Text fontSize="16px" color="#333" fontWeight="bold">{activeLesson?.title || "Selecciona una clase"}</Text>
                                <Text fontSize="14px" color="#D4AF37">Scarvaci Academy</Text>
                            </View>

                            {activeLesson?.content && activeLesson.content.includes('.') && (
                                <Button 
                                    onClick={() => downloadFile(activeLesson.content)}
                                    style={{ backgroundColor: '#D4AF37', color: 'black', fontWeight: 'bold' }}
                                >
                                    <FiDownload /> Descargar Material
                                </Button>
                            )}
                        </Flex>
                    </View>
                </Flex>

                <View width={isMobile ? "100%" : "350px"} padding="20px" style={{ borderLeft: '1px solid #eee', overflowY: 'auto', backgroundColor: '#fff' }}>
                    <Heading level={6} marginBottom="15px" color="#001f3f">CONTENIDO</Heading>
                    {lessons.map((l, idx) => (
                        <View 
                            key={l.id} 
                            onClick={() => setActiveLesson(l)}
                            style={{ 
                                cursor: 'pointer', padding: '12px', borderRadius: '8px', marginBottom: '8px',
                                backgroundColor: activeLesson?.id === l.id ? '#001f3f' : '#f4f4f4',
                                display: 'flex', alignItems: 'center', gap: '10px'
                            }}
                        >
                            <FiPlay color={activeLesson?.id === l.id ? '#D4AF37' : '#999'} />
                            <Text color={activeLesson?.id === l.id ? 'white' : '#333'} fontSize="13px">
                                {idx + 1}. {l.title}
                            </Text>
                        </View>
                    ))}
                </View>
            </Flex>

            {showNotes && (
                <Card 
                    style={{ 
                        position: 'fixed', right: 0, top: 0, bottom: 0, 
                        width: isMobile ? '100%' : '380px', zIndex: 1000,
                        display: 'flex', flexDirection: 'column', backgroundColor: '#fff',
                        boxShadow: '-10px 0 20px rgba(0,0,0,0.2)' 
                    }}
                >
                    <Flex justifyContent="space-between" padding="15px" backgroundColor="#001f3f">
                        <Heading level={5} color="white">Apuntes: {activeLesson?.title}</Heading>
                        <Button onClick={() => setShowNotes(false)} variation="link" color="white"><FiX /></Button>
                    </Flex>
                    <View padding="15px" borderBottom="1px solid #eee">
                        <TextAreaField labelHidden value={currentNote} onChange={e => setCurrentNote(e.target.value)} placeholder="Escribe aquí..." rows={3}/>
                        <Button marginTop="10px" onClick={handleSaveNote} width="100%" backgroundColor="#D4AF37" color="black" fontWeight="bold"><FiSave /> Guardar</Button>
                    </View>
                    <View flex="1" style={{ overflowY: 'auto' }} padding="15px">
                        {notesHistory.length === 0 ? (
                            <Text color="#aaa" textAlign="center" marginTop="20px">Sin apuntes guardados.</Text>
                        ) : (
                            notesHistory.map(note => (
                                <Card key={note.id} marginBottom="12px" padding="12px" borderRadius="8px" backgroundColor="#fffcf0" border="1px solid #f9ebbe">
                                    <Flex justifyContent="space-between">
                                        {/* CAMBIO QUIRÚRGICO: Ahora muestra Fecha y Hora en lugar de MOD */}
                                        <Text fontSize="10px" fontWeight="bold" color="#D4AF37">
                                            {new Date(note.createdAt).toLocaleString()}
                                        </Text>
                                        <FiTrash2 color="red" cursor="pointer" onClick={() => handleDeleteNote(note.id)} />
                                    </Flex>
                                    <Text fontSize="13px" marginTop="5px" color="#444">{note.content}</Text>
                                </Card>
                            ))
                        )}
                    </View>
                </Card>
            )}

            <Flex padding="5px" backgroundColor="#001f3f" justifyContent="center">
                <Text color="white" fontSize="9px"><FiShield color="#D4AF37" /> CONTENIDO PROTEGIDO</Text>
            </Flex>
        </View>
    );
};

export default CoursePlayer;